/**
 * Structured business information matching the backend AIBusinessInfoInputModel
 */
export interface BusinessInfo {
	location?: string;
	address?: string;
	website?: string;
	phoneNumber?: string;
	pronouns?: string;
	tonePreference?: string;
	description?: string;
}

/**
 * Parse business description text into structured BusinessInfo object
 * @param text - Raw business description text with labeled fields
 * @returns Parsed BusinessInfo object or null if no valid fields found
 */
export const parseBusinessDescription = (text: string): BusinessInfo | null => {
	if (!text || !text.trim()) {
		return null;
	}

	const lines = text.split('\n').map(line => line.trim());
	const businessInfo: BusinessInfo = {};
	let descriptionStartIndex = -1;

	// Parse structured fields
	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];

		if (line.startsWith('Location:')) {
			businessInfo.location = line.substring('Location:'.length).trim();
		} else if (line.startsWith('Address:')) {
			businessInfo.address = line.substring('Address:'.length).trim();
		} else if (line.startsWith('Website:')) {
			businessInfo.website = line.substring('Website:'.length).trim();
		} else if (line.startsWith('Phone number:')) {
			businessInfo.phoneNumber = line.substring('Phone number:'.length).trim();
		} else if (line.startsWith('Singular or Plural Pronouns:')) {
			businessInfo.pronouns = line.substring('Singular or Plural Pronouns:'.length).trim();
		} else if (line.startsWith('Tone preference:')) {
			businessInfo.tonePreference = line.substring('Tone preference:'.length).trim();
		} else if (line && !line.endsWith(':')) {
			// Found a line that's not a label - this starts the description
			descriptionStartIndex = i;
			break;
		}
	}

	// Collect remaining lines as description
	if (descriptionStartIndex >= 0) {
		businessInfo.description = lines.slice(descriptionStartIndex).join('\n').trim();
	}

	// Return null if no fields were parsed
	const hasAnyField = Object.values(businessInfo).some(value => value);
	return hasAnyField ? businessInfo : null;
};
